/**
 * passwordPolicy.ts — shared password policy + Cognito error-copy mapper (Phase 2.3)
 *
 * One source of truth so first-login, forgot-password reset, and change-password all
 * agree on what a valid password is and how Cognito errors read to the user.
 *
 * IMPORTANT: client-side validation is a UX affordance only. Cognito's user-pool policy
 * is the real enforcement point (it re-checks and returns InvalidPasswordException),
 * so this must mirror the pool policy, never replace it. LIVE pool policy: ≥8 chars,
 * with an uppercase letter and a number. Lowercase and symbols are NOT required.
 *
 * Being stricter than the pool is a real defect, not a safe extra: it refuses passwords
 * Cognito would accept, on the force-change form an invitee meets first. Two rules are
 * deliberately absent for that reason:
 *   - symbols: the stack sets requireSymbols:false (e.g. "Summer2026" is valid).
 *   - lowercase: the stack omits requireLowercase entirely, so the CDK renders the key as
 *     undefined and the pool is created without it. Verified against the live pool
 *     (RequireLowercase: false), NOT inferred from the CDK prop default — the "@default
 *     true" in the CDK docs describes Cognito's behaviour when no policy is supplied at all.
 */

export interface PasswordValidation {
  valid: boolean
  message?: string
}

/**
 * Validate a candidate password against the pool policy. Returns the first failing
 * rule's message (progressive disclosure) or { valid: true }.
 */
export function validatePassword(password: string): PasswordValidation {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters.' }
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must include an uppercase letter.' }
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Password must include a number.' }
  }
  // No lowercase check (T-415) and no symbol check (finding b) — the live pool requires
  // neither. Demanding either here would reject valid passwords the pool accepts.
  return { valid: true }
}

export type AuthErrorContext = 'signIn' | 'reset' | 'changePassword'

/**
 * Translate a Cognito error into friendly, context-aware copy. Matches on both the
 * error `name` (e.g. 'CodeMismatchException') and message text, since the library
 * surfaces codes inconsistently across flows.
 */
export function formatAuthError(err: unknown, context: AuthErrorContext = 'signIn'): string {
  const message = err instanceof Error ? err.message : ''
  const name = err instanceof Error ? err.name : ''
  const has = (needle: string) => name.includes(needle) || message.includes(needle)

  if (has('CodeMismatchException')) {
    return 'That code is incorrect. Please check the code from your email and try again.'
  }
  if (has('ExpiredCodeException')) {
    return 'That code has expired. Request a new code and try again.'
  }
  if (has('LimitExceededException') || has('TooManyRequestsException')) {
    return 'Too many attempts. Please wait a moment and try again.'
  }
  // Session-hydration failure during change-password (finding e): a distinct, normalized
  // signal from authService.changePassword. Must NOT read as "current password incorrect".
  if (has('SESSION_EXPIRED')) {
    return 'Your session has expired. Please sign in again.'
  }
  if (
    has('InvalidPasswordException') ||
    has('Password does not conform') ||
    has('PasswordPolicyViolation')
  ) {
    return 'Password does not meet the requirements: at least 8 characters with an uppercase letter and a number.'
  }
  if (has('UserNotConfirmedException')) {
    return 'Your account is not yet confirmed. Please contact your administrator.'
  }
  // Enumeration-safe reset (finding c): in the reset-confirm step a missing account must
  // read like a bad code, never reveal (non-)existence.
  if (context === 'reset' && has('UserNotFoundException')) {
    return 'That code is incorrect. Please check the code from your email and try again.'
  }
  if (
    has('NotAuthorizedException') ||
    has('Incorrect username or password') ||
    has('UserNotFoundException')
  ) {
    if (context === 'changePassword') {
      return 'Your current password is incorrect.'
    }
    return 'Incorrect email or password. Please try again.'
  }
  if (has('PasswordResetRequiredException')) {
    return 'A password reset is required. Use "Forgot password?" below to set a new one.'
  }
  return message || 'Something went wrong. Please try again.'
}
